import { Pane, Tablist, Tab, toaster } from 'evergreen-ui';
import { useRole } from '../contexts/RoleContext';
import TurniDayDetailModal from './Turni/TurniDayDetailModal';
import TurniShiftModal from './Turni/TurniShiftModal';
import TurniOrarioFissoModal from './Turni/TurniOrarioFissoModal';
import TurniFerieRequestModal from './Turni/TurniFerieRequestModal';
import TurniManagerFerieModal from './Turni/TurniManagerFerieModal';
import ReperibilitaModal from './Turni/ReperibilitaModal';
import TurniHeader from './Turni/TurniHeader';
import TurniRangesList from './Turni/TurniRangesList';
import TurniSidebar from './Turni/TurniSidebar';
import VistaCompatta from './Turni/Views/VistaCompatta';
import VistaMensile from './Turni/Views/VistaMensile';
import VistaSettimanale from './Turni/Views/VistaSettimanale';
import useWindowSize from '../utils/useWindowSize';
import useTurni, { EMPTY_RANGE } from './Turni/useTurni';
import useFerie from './Turni/useFerie';
import useTurniCalendar from './Turni/useTurniCalendar';
import useReperibilita, { EMPTY_REP } from './Turni/useReperibilita';
import useTurniModals from './Turni/useTurniModals';
import useTurniDragDrop from './Turni/useTurniDragDrop';
import TurniDropTypeDialog from './Turni/TurniDropTypeDialog';
import TurniDuplicateDialog from './Turni/TurniDuplicateDialog';
import { setActiveNightIntervals } from '../services/turniService';

export default function TurniOperatori({ collaborators: externalCollaborators }) {
  const { effectiveIsDirector: isDirector } = useRole();
  const isMobile = useWindowSize() < 768;

  const {
    userName, selectedMonth, setSelectedMonth,
    turniData, collaboratorsList,
    shiftInputs, setShiftInputs,
    loadData, handleSaveTurno, handleDeleteTurno, handleDuplicateTurno, handleTimbra,
    handleDragStart, handleDrop,
    shiftRanges, rangeInputs, setRangeInputs,
    handleSaveShiftRange, handleDeleteShiftRange,
  } = useTurni({ externalCollaborators });

  const { reperibilitaData, repInputs, setRepInputs, handleSaveReperibilita, handleDeleteReperibilita } = useReperibilita({ selectedMonth });
  const { managerFerieList, ferieInputs, setFerieInputs, handleRequestFerie, handleOpenManagerFerie, handleOpenMyFerie, handleUpdateFerieStatus } = useFerie({ userName, onFerieApproved: loadData });
  const { mockDays, getInitials, getBadgeProps, getStatusColor, coveragePercentage, coveredMonthHours, expectedMonthHours } = useTurniCalendar({ turniData, collaboratorsList, managerFerieList, selectedMonth });

  const {
    isDayModalOpen, setIsDayModalOpen,
    isTurnoModalOpen, setIsTurnoModalOpen,
    isFerieModalOpen, setIsFerieModalOpen,
    isManagerFerieOpen, setIsManagerFerieOpen,
    isOrarioFissoOpen, setIsOrarioFissoOpen,
    isReperibilitaModalOpen, setIsReperibilitaModalOpen,
    showRanges, setShowRanges, showOnlyUncovered, setShowOnlyUncovered,
    selectedDay, selectedWeek, setSelectedWeek, viewMode, setViewMode, handleDayClick,
  } = useTurniModals();

  const { isDragOverTrash, setIsDragOverTrash, isDropTypeOpen, setIsDropTypeOpen, pendingDropData, onDrop, pendingDuplicate, closeDuplicate, handleShiftDragStart, handleLeaveDragStart, handleTrashDrop } = useTurniDragDrop({ handleDrop, handleDeleteTurno, handleUpdateFerieStatus, turniData });

  const reperibilitaDay = selectedDay ? reperibilitaData.filter(r => r.date === selectedDay.fullDate) : [];

  const handleReperibilitaClick = (dateStr) => {
    setIsDayModalOpen(false);
    setRepInputs({ ...EMPTY_REP, date: dateStr });
    setIsReperibilitaModalOpen(true);
  };

  const handleEditReperibilita = (rep) => {
    setIsDayModalOpen(false);
    setRepInputs({ ...rep, start_time: rep.start_time?.substring(0, 5) || '', end_time: rep.end_time?.substring(0, 5) || '' });
    setIsReperibilitaModalOpen(true);
  };

  const handleSetActiveNightIntervals = async (shiftId, intervalli) => {
    try {
      await setActiveNightIntervals(shiftId, intervalli);
      await loadData();
    } catch (e) {
      toaster.danger('Errore salvataggio emergenza: ' + e.message);
    }
  };

  const openEditRange = (r) => {
    setRangeInputs({
      batch_id: r.batch_id, operator_name: r.operator_name,
      from_date: r.from_date, to_date: r.to_date,
      start_time: r.start_time ? r.start_time.substring(0, 5) : '',
      end_time: r.end_time ? r.end_time.substring(0, 5) : '',
      shift_type: r.shift_type || 'turno', is_overtime: !!Number(r.is_overtime),
      shift_period: r.shift_period || '', notes: r.notes || '',
    });
    setIsOrarioFissoOpen(true);
  };

  return (
    <Pane padding={24} maxWidth={1600} marginX="auto" width="100%">
      <TurniHeader
        selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth}
        coveragePercentage={coveragePercentage} coveredMonthHours={coveredMonthHours}
        expectedMonthHours={expectedMonthHours} showOnlyUncovered={showOnlyUncovered}
        setShowOnlyUncovered={setShowOnlyUncovered}
        onInsert={() => setIsTurnoModalOpen(true)}
        onOrarioFisso={() => { setRangeInputs(EMPTY_RANGE); setIsOrarioFissoOpen(true); }}
        onFerie={() => setIsFerieModalOpen(true)}
        onMieFerie={async () => { await handleOpenMyFerie(); setIsManagerFerieOpen(true); }}
        onGestioneFerie={async () => { await handleOpenManagerFerie(); setIsManagerFerieOpen(true); }}
      />

      {isDirector && <TurniRangesList shiftRanges={shiftRanges} showRanges={showRanges} setShowRanges={setShowRanges} onEdit={openEditRange} onDelete={handleDeleteShiftRange} />}

      <Pane display="flex" gap={24} flexDirection={isMobile ? 'column-reverse' : 'row'}>
        <Pane width={isMobile ? '100%' : 250} marginTop={isMobile ? 16 : 0}>
          <TurniSidebar collaboratorsList={collaboratorsList} getInitials={getInitials} viewMode={viewMode} isDragOverTrash={isDragOverTrash} setIsDragOverTrash={setIsDragOverTrash} onTrashDrop={handleTrashDrop} onDragStart={handleDragStart} />
        </Pane>
        <Pane flex={1} background="white" padding={16} borderRadius={8} border="default" overflowX="auto">
          <Tablist marginBottom={16}>
            <Tab isSelected={viewMode === 'compatta'} onSelect={() => setViewMode('compatta')}>Vista compatta</Tab>
            <Tab isSelected={viewMode === 'mensile'} onSelect={() => setViewMode('mensile')}>Vista mensile</Tab>
            {isDirector && <Tab isSelected={viewMode === 'settimanale'} onSelect={() => setViewMode('settimanale')}>Vista settimanale</Tab>}
          </Tablist>
          {viewMode === 'compatta' && <VistaCompatta mockDays={mockDays} showOnlyUncovered={showOnlyUncovered} handleDayClick={handleDayClick} getStatusColor={getStatusColor} />}
          {viewMode === 'mensile' && <VistaMensile mockDays={mockDays} showOnlyUncovered={showOnlyUncovered} turniData={turniData} managerFerieList={managerFerieList} reperibilitaData={reperibilitaData} getInitials={getInitials} getBadgeProps={getBadgeProps} handleDrop={onDrop} handleDayClick={handleDayClick} setShiftInputs={setShiftInputs} shiftInputs={shiftInputs} setIsTurnoModalOpen={setIsTurnoModalOpen} onShiftDragStart={handleShiftDragStart} onLeaveDragStart={handleLeaveDragStart} />}
          {viewMode === 'settimanale' && isDirector && <VistaSettimanale mockDays={mockDays} selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} collaboratorsList={collaboratorsList} turniData={turniData} managerFerieList={managerFerieList} reperibilitaData={reperibilitaData} getInitials={getInitials} getBadgeProps={getBadgeProps} handleTimbra={handleTimbra} />}
        </Pane>
      </Pane>

      <TurniDropTypeDialog
        isOpen={isDropTypeOpen} onClose={() => setIsDropTypeOpen(false)} pendingDropData={pendingDropData}
        onDropAsTurno={() => { setIsDropTypeOpen(false); setIsTurnoModalOpen(true); }}
        onDropAsFerie={() => { setIsDropTypeOpen(false); setFerieInputs(prev => ({ ...prev, operator_name: pendingDropData.name, start_date: pendingDropData.date, end_date: pendingDropData.date })); setIsFerieModalOpen(true); }}
        onDropAsRep={() => { setIsDropTypeOpen(false); setRepInputs({ ...EMPTY_REP, operator_name: pendingDropData.name, date: pendingDropData.date }); setIsReperibilitaModalOpen(true); }}
      />

      <TurniDuplicateDialog
        pendingDuplicate={pendingDuplicate} turniData={turniData} onClose={closeDuplicate}
        onConfirm={async (close) => { const ok = await handleDuplicateTurno(pendingDuplicate.shift, pendingDuplicate.targetDate); if (ok) close(); }}
      />

      <TurniDayDetailModal
        isOpen={isDayModalOpen} onClose={() => setIsDayModalOpen(false)} selectedDay={selectedDay}
        onRemoveShift={async (id) => { try { await handleDeleteTurno(id); setIsDayModalOpen(false); } catch (_e) {} }}
        onAssignClick={(dateStr) => { setIsDayModalOpen(false); if (isDirector) { setShiftInputs(prev => ({ ...prev, date: dateStr })); setIsTurnoModalOpen(true); } }}
        onSetActiveNightIntervals={handleSetActiveNightIntervals}
        reperibilitaDay={reperibilitaDay}
        onRemoveReperibilita={async (id) => { try { await handleDeleteReperibilita(id); } catch (_e) {} }}
        onReperibilitaClick={handleReperibilitaClick} onEditReperibilita={handleEditReperibilita}
      />
      <TurniShiftModal isOpen={isTurnoModalOpen} onClose={() => setIsTurnoModalOpen(false)} onSave={async () => { const ok = await handleSaveTurno(); if (ok) setIsTurnoModalOpen(false); }} shiftInputs={shiftInputs} setShiftInputs={setShiftInputs} userName={userName} collaboratorsList={collaboratorsList} turniData={turniData} />
      <TurniOrarioFissoModal isOpen={isOrarioFissoOpen} onClose={() => setIsOrarioFissoOpen(false)} onSave={async () => { const ok = await handleSaveShiftRange(); if (ok) setIsOrarioFissoOpen(false); }} rangeInputs={rangeInputs} setRangeInputs={setRangeInputs} userName={userName} collaboratorsList={collaboratorsList} />
      <TurniFerieRequestModal isOpen={isFerieModalOpen} onClose={() => setIsFerieModalOpen(false)} onRequest={async () => { const ok = await handleRequestFerie(); if (ok) setIsFerieModalOpen(false); }} ferieInputs={ferieInputs} setFerieInputs={setFerieInputs} collaboratorsList={collaboratorsList} selfName={userName} />
      <TurniManagerFerieModal isOpen={isManagerFerieOpen} onClose={() => setIsManagerFerieOpen(false)} managerFerieList={managerFerieList} onUpdateStatus={handleUpdateFerieStatus} />
      <ReperibilitaModal isOpen={isReperibilitaModalOpen} onClose={() => { setIsReperibilitaModalOpen(false); setRepInputs(EMPTY_REP); }} onSave={async () => { const ok = await handleSaveReperibilita(); if (ok) setIsReperibilitaModalOpen(false); }} repInputs={repInputs} setRepInputs={setRepInputs} collaboratorsList={collaboratorsList} />
    </Pane>
  );
}
